'use client';

import React, { useState } from 'react';
import { formatBaseIdDisplay } from '@/app/lib/utils';
import CourseName from '@/app/Components/CourseName';
import { CourseLink } from '@/app/Components/CoursePeek/CourseLink';

interface CourseListRendererProps {
  data?: any;
  metadata?: any;
}

const formatCampuses = (campuses: any) => {
  if (!campuses) return 'TBA';
  if (Array.isArray(campuses)) return campuses.join(', ');
  return campuses;
};

const formatInstructors = (instructors: any) => {
  if (!instructors) return '';
  const values = Array.isArray(instructors) ? instructors : [instructors];
  return values
    .map((instructor) => typeof instructor === 'string' ? instructor : instructor?.name)
    .filter(Boolean)
    .join(', ');
};

const formatMinute = (value: unknown) => {
  if (typeof value !== 'number') return 'TBA';
  const hour = Math.floor(value / 60);
  const minute = value % 60;
  return `${hour % 12 || 12}:${String(minute).padStart(2, '0')} ${hour >= 12 ? 'PM' : 'AM'}`;
};

const formatMeetings = (meetings: any) => {
  if (!Array.isArray(meetings) || meetings.length === 0) return 'Times TBA';
  return meetings
    .map((meeting) => `${meeting.day || 'TBA'} ${formatMinute(meeting.startMinute)}–${formatMinute(meeting.endMinute)}${meeting.location ? ` · ${meeting.location}` : ''}`)
    .join('; ');
};

const courseIdentity = (course: any, index: number) =>
  `${course.baseId || course.base_id || index}:${course.section || course.sectionNumber || index}`;

export function CourseListRenderer({ data, metadata }: CourseListRendererProps) {
  const [comparisonIds, setComparisonIds] = useState<string[]>([]);
  if (!data) {
    return (
      <div className="flex h-full items-center justify-center text-sm text-[var(--ss-text-muted)]">
        No course results to show yet.
      </div>
    );
  }

  const courses = Array.isArray(data.courses) ? data.courses : [];
  const courseLists = Array.isArray(data.lists) ? data.lists : [];
  const source = metadata?.source || 'Rutgers course catalog';
  const evidence = data.freshness ?? metadata?.freshness;
  const freshness = evidence?.observedAt || evidence?.snapshotAt || 'Observation time unknown';
  const comparedCourses = courses.filter((course: any, index: number) => comparisonIds.includes(courseIdentity(course, index)));
  const toggleComparison = (id: string) => {
    setComparisonIds((current) => current.includes(id)
      ? current.filter((candidate) => candidate !== id)
      : current.length < 3 ? [...current, id] : [...current.slice(1), id]);
  };

  if (courseLists.length > 0 && courses.length === 0) {
    return (
      <div className="space-y-4 p-4">
        <div className="rounded-card border border-[var(--ss-border)] bg-[var(--ss-surface)] p-4 shadow-e1">
          <h3 className="text-lg font-semibold text-[var(--ss-text)]">Course Lists</h3>
          <p className="text-xs text-[var(--ss-text-muted)]">
            {courseLists.length} list{courseLists.length !== 1 ? 's' : ''} found
          </p>
        </div>
        <div className="grid gap-3">
          {courseLists.map((list: any) => (
            <div key={list.id} className="rounded-card border border-[var(--ss-border)] bg-[var(--ss-surface)] p-4 shadow-e1">
              <div className="flex items-center justify-between gap-3">
                <div>
                  <p className="text-sm font-semibold text-[var(--ss-text)]">{list.name || list.title}</p>
                  {list.metadata?.code && (
                    <p className="text-xs text-[var(--ss-text-muted)]">Code: {list.metadata.code}</p>
                  )}
                </div>
                <span className="text-xs text-[var(--ss-text-muted)]">{list.class_count ?? list.count ?? (list.classes?.length || 0)} courses</span>
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="flex h-full flex-col">
      <div className="border-b border-[var(--ss-border)] bg-[var(--ss-surface)] px-4 py-3">
        <div className="flex flex-col gap-2 sm:flex-row sm:items-start sm:justify-between sm:gap-3">
          <div>
            <h3 className="text-lg font-semibold text-[var(--ss-text)]">Course Results</h3>
            <p className="text-xs text-[var(--ss-text-muted)]">
              {courses.length} section{courses.length !== 1 ? 's' : ''} shown
            </p>
          </div>
          <div className="text-left text-[10px] leading-4 text-[var(--ss-text-muted)] sm:text-right">
            <p className="font-semibold">{evidence?.kind === 'live' ? 'Live observation' : evidence?.kind === 'snapshot' ? 'Catalog snapshot' : 'Freshness unknown'}</p>
            <p>{source} · {freshness}</p><p>{evidence?.warning || 'Availability is not verified live. Check Rutgers WebReg.'}</p><a href="https://classes.rutgers.edu/soc/" target="_blank" rel="noreferrer" className="underline">Rutgers Schedule of Classes</a>
          </div>
        </div>
        {comparedCourses.length > 0 && (
          <div className="mt-3 rounded-control border border-[var(--ss-ai)] bg-[var(--ss-ai-bg)] px-3 py-2">
            <div className="flex items-center justify-between gap-2">
              <p className="text-xs font-semibold text-[var(--ss-text)]">Compare {comparedCourses.length} selected</p>
              <button type="button" onClick={() => setComparisonIds([])} className="min-h-12 text-[11px] font-medium text-[var(--ss-ai)] hover:underline">Clear</button>
            </div>
            <p className="mt-1 text-[11px] text-[var(--ss-text-muted)]">
              {comparedCourses.map((course: any) => formatBaseIdDisplay(course.baseId || course.base_id || '')).filter(Boolean).join(' · ')}
            </p>
          </div>
        )}
      </div>

      <div className="flex-1 overflow-y-auto px-4 py-4 space-y-3">
        {courses.map((course: any, index: number) => {
          const baseId = course.baseId || course.base_id || '';
          const comparisonId = courseIdentity(course, index);
          const isSelected = comparisonIds.includes(comparisonId);
          return (
            <div key={`${baseId}-${course.section || index}`} className={`rounded-card border bg-[var(--ss-surface)] p-4 shadow-e1 ${isSelected ? 'border-[var(--ss-ai)] ring-1 ring-[var(--ss-focus)]' : 'border-[var(--ss-border)]'}`}>
              <div className="flex items-start justify-between gap-4">
                <div>
                  <p className="text-sm font-semibold text-[var(--ss-text)]">
                    {baseId ? <CourseLink courseId={String(baseId)} context={{ label: 'From this assistant result', facts: [{ label: 'Section', value: course.section || 'TBA' }, { label: 'Meetings', value: formatMeetings(course.meetingTimes) }, ...(course.instructors ? [{ label: 'Instructors', value: formatInstructors(course.instructors) }] : [])] }}>{course.courseName || course.fullTitle || course.title || course.name || 'Untitled Course'}</CourseLink> : course.courseName || course.fullTitle || course.title || course.name || 'Untitled Course'}
                  </p>
                  <p className="mt-1 text-xs text-[var(--ss-text-muted)]">
                    {formatBaseIdDisplay(baseId)} · {course.credits ?? 'TBA'} credits
                  </p>
                  {course.department && course.classNumber && (
                    <p className="mt-1 text-xs text-[var(--ss-text-muted)]">
                      {course.department} {course.classNumber}
                    </p>
                  )}
                </div>
                <div className={`text-[10px] font-semibold px-2.5 py-1 rounded-full border ${course.isOpen === false
                    ? 'border-[var(--ss-critical)] bg-[var(--ss-critical-bg)] text-[var(--ss-critical)]'
                    : course.isOpen === true ? 'border-[var(--ss-positive)] bg-[var(--ss-positive-bg)] text-[var(--ss-positive)]' : 'border-[var(--ss-border)] text-[var(--ss-text-muted)]'
                  }`}>
                  {course.isOpen === false ? 'Closed' : course.isOpen === true ? 'Open' : 'Availability unknown'}
                </div>
              </div>

              <button
                type="button"
                onClick={() => toggleComparison(comparisonId)}
                aria-pressed={isSelected}
                className={`mt-3 min-h-12 rounded-control border px-3 text-[11px] font-semibold transition-colors ${isSelected ? 'border-[var(--ss-ai)] bg-[var(--ss-ai-bg)] text-[var(--ss-ai)]' : 'border-[var(--ss-border)] text-[var(--ss-text-muted)] hover:border-[var(--ss-ai)] hover:text-[var(--ss-ai)]'}`}
              >
                {isSelected ? 'Selected for comparison' : 'Compare'}
              </button>

              <div className="mt-3 grid gap-2 text-xs text-[var(--ss-text-muted)]">
                <div>
                  <span className="font-semibold text-[var(--ss-text)]">Section:</span>{' '}
                  {course.section || 'TBA'} · {formatMeetings(course.meetingTimes)}
                </div>
                <div>
                  <span className="font-semibold text-[var(--ss-text)]">Campus:</span>{' '}
                  {formatCampuses(course.campuses || course.campus)}
                </div>
                {course.instructors && course.instructors.length > 0 && (
                  <div>
                    <span className="font-semibold text-[var(--ss-text)]">Instructors:</span>{' '}
                    {formatInstructors(course.instructors)}
                  </div>
                )}
              </div>

              {baseId && (
                <div className="mt-3">
                  <CourseName code={baseId} className="text-xs text-[var(--ss-text-muted)]" />
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
